import { fromEntries } from "./browserCompat";
const STYLE_KEYS = Object.freeze([
  "backgroundStyle",
  "logoStyle",
  "logoSurfaceStyle",
  "dividerStyle",
  "eyebrowStyle",
  "titleStyle",
  "descriptionStyle",
  "ctaStyle",
  "decorationLeftStyle",
  "decorationRightStyle",
]);

const LEGACY_ELEMENT_PATHS = new Set([
  "catalog.spotlight.eyebrow",
  "catalog.spotlight.title",
  "catalog.spotlight.description",
  "catalog.spotlight.ctaLabel",
]);

function clone(value) {
  if (Array.isArray(value)) return value.map(clone);
  if (!value || typeof value !== "object") return value;
  return fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]));
}

function objectValue(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function hasValue(value) {
  return value !== undefined && value !== null && !(typeof value === "string" && value.trim() === "");
}

function pick(...values) {
  return values.find(hasValue);
}

export function legacySpotlightColorWithAlpha(value, alphaHex) {
  const candidate = String(value || "").trim();
  if (!candidate || candidate.toLowerCase() === "transparent") return "transparent";
  if (/^#[0-9a-f]{6}$/i.test(candidate)) return `${candidate}${alphaHex}`;
  if (/^#[0-9a-f]{3}$/i.test(candidate)) {
    const expanded = `#${candidate.slice(1).split("").map((char) => `${char}${char}`).join("")}`;
    return `${expanded}${alphaHex}`;
  }
  return candidate;
}

export function createSpotlightVisualDefaults(theme = {}) {
  const primary = pick(theme?.primaryColor, "#F35A3A");
  const secondary = pick(theme?.secondaryColor, "#19242F");
  return {
    backgroundStyle: {
      startColor: secondary,
      endColor: primary,
      angle: 100,
      overlayStartColor: legacySpotlightColorWithAlpha(secondary, "F2"),
      overlayEndColor: legacySpotlightColorWithAlpha(primary, "D9"),
      overlayAngle: 90,
      imageFit: "scaled",
    },
    logoStyle: { objectFit: "contain", mixBlendMode: "multiply" },
    logoSurfaceStyle: {
      backgroundColor: "#FFFFFF",
      borderColor: "transparent",
      borderWidth: 0,
      borderRadius: 16,
      boxShadow: "0 10px 30px rgba(0,0,0,.28)",
    },
    dividerStyle: { color: "rgba(255,255,255,.25)" },
    eyebrowStyle: { color: "rgba(255,255,255,.72)" },
    titleStyle: { color: "#FFFFFF", textShadow: "0 3px 14px rgba(0,0,0,.35)" },
    descriptionStyle: { color: "rgba(255,255,255,.76)" },
    ctaStyle: {
      backgroundColor: "#FFFFFF",
      textColor: primary,
      hoverBackgroundColor: "rgba(255,255,255,.90)",
      hoverTextColor: primary,
      borderColor: "transparent",
      borderWidth: 0,
      boxShadow: "0px 3px 1px -2px rgba(0,0,0,.20), 0px 2px 2px 0px rgba(0,0,0,.14), 0px 1px 5px 0px rgba(0,0,0,.12)",
      hoverBoxShadow: "0px 2px 4px -1px rgba(0,0,0,.20), 0px 4px 5px 0px rgba(0,0,0,.14), 0px 1px 10px 0px rgba(0,0,0,.12)",
    },
    decorationLeftStyle: { startColor: "rgba(255,255,255,.18)", endColor: "transparent", endStop: 68 },
    decorationRightStyle: { startColor: "rgba(0,0,0,.18)", endColor: "transparent", endStop: 70 },
  };
}

export const SPOTLIGHT_VISUAL_DEFAULTS = Object.freeze(createSpotlightVisualDefaults());

function effectiveLegacyColor(spotlight, visualStyle, theme, key, fallback) {
  const legacyKey = key === "primary" ? "primaryColor" : "secondaryColor";
  const themeKey = key === "primary" ? "primaryColor" : "secondaryColor";
  const localPalette = visualStyle?.useGlobalColors === false ? visualStyle?.colors?.[key] : undefined;
  return pick(spotlight?.[legacyKey], localPalette, theme?.[themeKey], fallback);
}

function legacyElementStyle(visualStyle, path) {
  return objectValue(visualStyle?.elementColors?.[path]);
}

function styleOrLegacy(source, key, legacyValue, fixedDefault) {
  if (source?.[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
    return { ...clone(fixedDefault), ...clone(source[key]) };
  }
  return { ...clone(fixedDefault), ...clone(legacyValue) };
}

export function materializeSpotlightVisualOwnership(value = {}, theme = {}, legacyVisualStyle = {}) {
  const source = clone(objectValue(value));
  const visualStyle = objectValue(legacyVisualStyle);
  const fixedDefaults = createSpotlightVisualDefaults();
  const primary = effectiveLegacyColor(source, visualStyle, theme, "primary", "#F35A3A");
  const secondary = effectiveLegacyColor(source, visualStyle, theme, "secondary", "#19242F");
  const legacyDefaults = createSpotlightVisualDefaults({ primaryColor: primary, secondaryColor: secondary });
  const canonicalBackground = Boolean(source.backgroundStyle && typeof source.backgroundStyle === "object" && !Array.isArray(source.backgroundStyle));
  const legacyVisualBackground = !canonicalBackground && hasValue(visualStyle?.backgroundImageUrl)
    ? String(visualStyle.backgroundImageUrl).trim()
    : "";
  const backgroundImageUrl = legacyVisualBackground || source.backgroundImageUrl || "";
  const backgroundImagePosition = legacyVisualBackground
    ? (visualStyle.backgroundImagePosition || source.backgroundImagePosition || "center")
    : (source.backgroundImagePosition || "center");
  const eyebrowOverride = legacyElementStyle(visualStyle, "catalog.spotlight.eyebrow");
  const titleOverride = legacyElementStyle(visualStyle, "catalog.spotlight.title");
  const descriptionOverride = legacyElementStyle(visualStyle, "catalog.spotlight.description");
  const ctaOverride = legacyElementStyle(visualStyle, "catalog.spotlight.ctaLabel");

  const backgroundLegacy = {
    ...legacyDefaults.backgroundStyle,
    ...(legacyVisualBackground
      ? { overlayStartColor: "transparent", overlayEndColor: "transparent", imageFit: "cover" }
      : {}),
  };
  const titleLegacy = {
    ...legacyDefaults.titleStyle,
    ...(hasValue(titleOverride.color) ? { color: titleOverride.color } : {}),
    ...(hasValue(titleOverride.shadow) ? { textShadow: `0 2px 10px ${titleOverride.shadow}` } : {}),
  };
  const ctaLegacy = {
    ...legacyDefaults.ctaStyle,
    ...(hasValue(ctaOverride.background) ? { backgroundColor: ctaOverride.background } : {}),
    ...(hasValue(ctaOverride.color) ? { textColor: ctaOverride.color } : {}),
    ...(hasValue(ctaOverride.hoverBackground) ? { hoverBackgroundColor: ctaOverride.hoverBackground } : {}),
    ...(hasValue(ctaOverride.hoverColor) ? { hoverTextColor: ctaOverride.hoverColor } : {}),
    ...(hasValue(ctaOverride.borderColor) ? { borderColor: ctaOverride.borderColor } : {}),
    ...(hasValue(ctaOverride.shadow) ? { boxShadow: `0 8px 24px ${ctaOverride.shadow}` } : {}),
  };

  source.backgroundImageUrl = backgroundImageUrl;
  source.backgroundImagePosition = backgroundImagePosition;
  source.backgroundStyle = styleOrLegacy(source, "backgroundStyle", backgroundLegacy, fixedDefaults.backgroundStyle);
  source.logoStyle = styleOrLegacy(source, "logoStyle", legacyDefaults.logoStyle, fixedDefaults.logoStyle);
  source.logoSurfaceStyle = styleOrLegacy(source, "logoSurfaceStyle", legacyDefaults.logoSurfaceStyle, fixedDefaults.logoSurfaceStyle);
  source.dividerStyle = styleOrLegacy(source, "dividerStyle", legacyDefaults.dividerStyle, fixedDefaults.dividerStyle);
  source.eyebrowStyle = styleOrLegacy(source, "eyebrowStyle", {
    ...legacyDefaults.eyebrowStyle,
    ...(hasValue(eyebrowOverride.color) ? { color: eyebrowOverride.color } : {}),
  }, fixedDefaults.eyebrowStyle);
  source.titleStyle = styleOrLegacy(source, "titleStyle", titleLegacy, fixedDefaults.titleStyle);
  source.descriptionStyle = styleOrLegacy(source, "descriptionStyle", {
    ...legacyDefaults.descriptionStyle,
    ...(hasValue(descriptionOverride.color) ? { color: descriptionOverride.color } : {}),
  }, fixedDefaults.descriptionStyle);
  source.ctaStyle = styleOrLegacy(source, "ctaStyle", ctaLegacy, fixedDefaults.ctaStyle);
  source.decorationLeftStyle = styleOrLegacy(source, "decorationLeftStyle", legacyDefaults.decorationLeftStyle, fixedDefaults.decorationLeftStyle);
  source.decorationRightStyle = styleOrLegacy(source, "decorationRightStyle", legacyDefaults.decorationRightStyle, fixedDefaults.decorationRightStyle);
  delete source.primaryColor;
  delete source.secondaryColor;
  return source;
}

export function stripSpotlightLegacyVisualStyle(value = {}) {
  const source = clone(objectValue(value));
  delete source.backgroundImageUrl;
  delete source.backgroundImagePosition;
  delete source.useGlobalColors;
  delete source.colors;
  if (source.elementColors && typeof source.elementColors === "object" && !Array.isArray(source.elementColors)) {
    const nextElementColors = {};
    Object.entries(source.elementColors).forEach(([path, raw]) => {
      const item = clone(objectValue(raw));
      if (LEGACY_ELEMENT_PATHS.has(path)) {
        ["color", "background", "borderColor", "shadow", "hoverColor", "hoverBackground"].forEach((key) => delete item[key]);
      }
      if (Object.keys(item).length) nextElementColors[path] = item;
    });
    if (Object.keys(nextElementColors).length) source.elementColors = nextElementColors;
    else delete source.elementColors;
  }
  return source;
}

export function materializeSpotlightSettings(input = {}) {
  const source = clone(objectValue(input));
  const theme = objectValue(source.theme);
  source.catalog = objectValue(source.catalog);
  const segments = Array.isArray(source.layout?.segments) ? source.layout.segments : [];
  const canonicalSegment = segments.find((segment) => (segment?.type || segment?.id) === "spotlight" && (segment?.id || "spotlight") === "spotlight");
  source.catalog.spotlight = materializeSpotlightVisualOwnership(
    objectValue(source.catalog.spotlight),
    theme,
    canonicalSegment?.visualStyle,
  );

  if (segments.length) {
    source.layout = {
      ...objectValue(source.layout),
      segments: segments.map((rawSegment) => {
        const segment = clone(objectValue(rawSegment));
        if ((segment.type || segment.id) !== "spotlight") return segment;
        const canonical = (segment.id || "spotlight") === "spotlight";
        if (!canonical) {
          const data = clone(objectValue(segment.data));
          data.catalog = objectValue(data.catalog);
          const instanceSpotlight = clone(objectValue(data.catalog.spotlight));
          const snapshot = {
            ...clone(source.catalog.spotlight),
            ...instanceSpotlight,
          };
          if (Object.keys(instanceSpotlight).length) {
            STYLE_KEYS.forEach((key) => {
              if (!Object.prototype.hasOwnProperty.call(instanceSpotlight, key)) delete snapshot[key];
            });
          }
          data.catalog.spotlight = materializeSpotlightVisualOwnership(snapshot, objectValue(data.theme || theme), segment.visualStyle);
          segment.data = data;
        }
        if (segment.visualStyle) segment.visualStyle = stripSpotlightLegacyVisualStyle(segment.visualStyle);
        return segment;
      }),
    };
  }
  return source;
}

export function spotlightLinearGradient(style = {}, overlay = false) {
  const source = objectValue(style);
  const startColor = overlay ? source.overlayStartColor : source.startColor;
  const endColor = overlay ? source.overlayEndColor : source.endColor;
  const angle = Number(overlay ? source.overlayAngle : source.angle);
  return `linear-gradient(${Number.isFinite(angle) ? angle : (overlay ? 90 : 100)}deg, ${String(startColor ?? "").trim()} 0%, ${String(endColor ?? "").trim()} 100%)`;
}

export function spotlightRadialGradient(style = {}) {
  const source = objectValue(style);
  const stop = Number(source.endStop);
  return `radial-gradient(circle, ${String(source.startColor ?? "").trim()} 0%, ${String(source.endColor ?? "").trim()} ${Number.isFinite(stop) ? Math.max(0, Math.min(100, stop)) : 70}%)`;
}

export function spotlightVisualStyleKeys() {
  return [...STYLE_KEYS];
}
